// Le circuit d'écriture commun à toutes les corrections du DSI :
//   1. simulation (POST ?simulation=true) → différence exacte, rien n'est écrit ;
//   2. validation par le DSI → écriture avec clé d'idempotence et If-Match (version lue) ;
//   3. conflit 412 → l'élément a changé : on montre ce qui a changé et on repart de la version actuelle.
// Un nouvel essai après une coupure réseau réutilise la même clé : l'API ne réécrit pas, elle rejoue la réponse.
import { useState } from 'react';
import { ErreurApi, expliquerErreur, nouvelleCle, type ResultatEcriture } from '../api/client';
import type { Correction, Lien, Objet, ReponseEcriture } from '../api/types';
import { useEkera, useRelire } from '../contexte';
import { differenceLocale } from '../domaine/corrections';
import { LIBELLE_OPERATION } from '../domaine/format';
import { Difference, Erreur } from './communs';

export type EtatEcriture =
  | { etape: 'saisie' }
  | { etape: 'simulation'; correction: Correction; reponse: ReponseEcriture; cle: string }
  | { etape: 'ecriture'; correction: Correction; reponse: ReponseEcriture; cle: string }
  | { etape: 'ecrite'; resultat: ResultatEcriture }
  | { etape: 'conflit'; correction: Correction; luAvant: Objet | Lien | null; actuel: { element: Objet | Lien; etag: string } | null }
  | { etape: 'echec'; correction: Correction; reponse: ReponseEcriture; cle: string; erreur: unknown };

export function useEcriture(ifMatch: string | undefined, luAvant: Objet | Lien | null) {
  const { client, cid } = useEkera();
  const relire = useRelire();
  const [etat, setEtat] = useState<EtatEcriture>({ etape: 'saisie' });
  const [erreurSimulation, setErreurSimulation] = useState<unknown>(null);
  const [occupe, setOccupe] = useState(false);

  async function versConflit(correction: Correction) {
    let actuel: { element: Objet | Lien; etag: string } | null = null;
    if (correction.element) {
      try {
        const nature = luAvant && 'source' in luAvant ? 'lien' : 'objet';
        actuel = await client.element(cid, correction.element, nature);
      } catch {
        actuel = null;
      }
    }
    setEtat({ etape: 'conflit', correction, luAvant, actuel });
  }

  async function simuler(correction: Correction) {
    setOccupe(true);
    setErreurSimulation(null);
    try {
      const r = await client.corriger(cid, correction, { simulation: true, ifMatch });
      // Une clé par écriture proposée : elle sera réutilisée telle quelle si l'écriture doit être retentée.
      setEtat({ etape: 'simulation', correction, reponse: r.reponse, cle: nouvelleCle() });
    } catch (e) {
      if (e instanceof ErreurApi && e.estConflit) await versConflit(correction);
      else setErreurSimulation(e);
    } finally {
      setOccupe(false);
    }
  }

  async function ecrire() {
    if (etat.etape !== 'simulation' && etat.etape !== 'echec') return;
    const { correction, reponse, cle } = etat;
    setOccupe(true);
    setEtat({ etape: 'ecriture', correction, reponse, cle });
    try {
      const r = await client.corriger(cid, correction, { simulation: false, cleIdempotence: cle, ifMatch });
      setEtat({ etape: 'ecrite', resultat: r });
      await relire();
    } catch (e) {
      if (e instanceof ErreurApi && e.estConflit) await versConflit(correction);
      else setEtat({ etape: 'echec', correction, reponse, cle, erreur: e });
    } finally {
      setOccupe(false);
    }
  }

  return { etat, setEtat, simuler, ecrire, occupe, erreurSimulation, reinitialiser: () => setEtat({ etape: 'saisie' }) };
}

const ETAPES = ['Proposer', 'Simulation', 'Écriture'];

export function EtapesEcriture({ etat }: { etat: EtatEcriture }) {
  const i = etat.etape === 'saisie' || etat.etape === 'conflit' ? 0 : etat.etape === 'simulation' || etat.etape === 'echec' ? 1 : 2;
  return (
    <ol className="etapes" aria-label="Étapes de l’écriture">
      {ETAPES.map((e, j) => (
        <li key={e} aria-current={j === i ? 'step' : undefined} className={j < i || etat.etape === 'ecrite' ? 'fait' : ''}>
          {j + 1}. {e}
        </li>
      ))}
    </ol>
  );
}

/** Panneau de simulation, de validation, de conflit et de résultat. */
export function PanneauEcriture({
  ecr,
  surReprendre,
  libelleValider = 'Valider l’écriture',
}: {
  ecr: ReturnType<typeof useEcriture>;
  /** Conflit : repartir de la version actuelle (le formulaire garde la saisie du DSI). */
  surReprendre: (actuel: { element: Objet | Lien; etag: string }) => void;
  libelleValider?: string;
}) {
  const { etat } = ecr;
  if (etat.etape === 'saisie') {
    return ecr.erreurSimulation ? <Erreur erreur={ecr.erreurSimulation} titre="La simulation a été refusée." /> : null;
  }
  if (etat.etape === 'conflit') {
    const changes = etat.luAvant && etat.actuel ? differenceLocale(etat.luAvant, etat.actuel.element, etat.actuel.element.id) : [];
    return (
      <section className="encadre attention pile apparait" role="alert" aria-labelledby="t-conflit" data-testid="conflit">
        <h2 id="t-conflit">L’élément a changé depuis votre lecture</h2>
        <p>
          Quelqu’un (ou un connecteur) l’a modifié après que vous l’avez lu
          {etat.luAvant && etat.actuel ? ` : version lue ${etat.luAvant.version ?? 1}, version actuelle ${etat.actuel.element.version ?? 1}` : ''}.
          Rien n’a été écrit : Ekera n’écrase jamais une modification qu’il n’a pas vue.
        </p>
        {etat.actuel && etat.luAvant && (
          <>
            <h3>Ce qui a changé depuis votre lecture</h3>
            <Difference difference={changes} element={etat.actuel.element.id} />
          </>
        )}
        <div className="ligne">
          {etat.actuel && (
            <button type="button" className="btn principal" onClick={() => surReprendre(etat.actuel!)}>
              Reprendre sur la version actuelle
            </button>
          )}
          <button type="button" className="btn" onClick={ecr.reinitialiser}>
            Abandonner
          </button>
        </div>
      </section>
    );
  }
  if (etat.etape === 'ecrite') {
    const r = etat.resultat.reponse;
    return (
      <section className="encadre ok pile apparait" role="status" data-testid="ecriture-faite">
        <p>
          <b>
            {LIBELLE_OPERATION[r.operation]} : écriture {r.ecriture} enregistrée
            {etat.resultat.rejoue ? ' (déjà faite : l’API a rejoué la réponse, rien n’a été réécrit)' : ''}.
          </b>
        </p>
        <p>
          Élément <span className="num">{r.element}</span>, version {r.version} · révision de la carte {r.revision} · événement{' '}
          <span className="num">{r.evenement}</span>. Voir le <a href="#/exports">journal des écritures</a>.
        </p>
        <div className="ligne">
          <button type="button" className="btn" onClick={ecr.reinitialiser}>
            Nouvelle proposition
          </button>
        </div>
      </section>
    );
  }
  const { reponse } = etat;
  return (
    <section className="panneau pile apparait" aria-labelledby="t-simu" data-testid="simulation">
      <div className="panneau-tete">
        <h2 id="t-simu">Simulation : différence exacte</h2>
        <span className="indice">Rien n’est encore écrit</span>
      </div>
      <p className="note">
        {LIBELLE_OPERATION[reponse.operation]} <span className="num">{reponse.element}</span> : version {reponse.version} après
        l’écriture.
      </p>
      <Difference difference={reponse.difference} element={reponse.element} />
      {etat.etape === 'echec' && (
        <>
          <Erreur erreur={etat.erreur} titre="L’écriture n’a pas abouti." />
          <p className="note">
            Un nouvel essai réutilise la même clé d’idempotence : si la première écriture a en fait abouti, l’API ne la refait pas.
          </p>
        </>
      )}
      <div className="ligne">
        <button type="button" className="btn principal" disabled={ecr.occupe} onClick={() => void ecr.ecrire()}>
          {etat.etape === 'echec' ? 'Réessayer l’écriture' : ecr.occupe ? 'Écriture…' : libelleValider}
        </button>
        <button type="button" className="btn" disabled={ecr.occupe} onClick={ecr.reinitialiser}>
          Modifier la proposition
        </button>
      </div>
      <p className="note">
        Clé d’idempotence : <span className="num">{etat.cle}</span>
      </p>
    </section>
  );
}

export { expliquerErreur };
